import { useEffect, useState } from "react";

import { FloatingPortal } from "@floating-ui/react";
import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import {
  NodeTextField,
  type Props as NodeTextFieldProps,
} from "@/components/doc/right-sidebar/active-node-style/fields/NodeTextField";
import { Suggestions, type Props as SuggestionsProps } from "@/components/suggestions/Suggestions";
import {
  SuggestionsProvider,
  useSuggestionsContext,
} from "@/components/suggestions/SuggestionsProvider";
import { useSuggestionsFloating } from "@/components/suggestions/useSuggestionsFloating";
import { keyMatcher } from "@/utils/keyMatcher";
import { twMerge } from "@/utils/twMerge";

import type {
  ProviderSearchOptions,
  SingleSuggestion,
  Suggestion,
  SuggestionProviderType,
} from "@/components/suggestions/types";
import type { DocumentModel } from "@/models/DocumentModel";

export type ValueSuggestionsProps = Pick<
  SuggestionsProps,
  "initialIndex" | "providerSearchOptions" | "accept"
> & {
  onSelect: (suggestion: SingleSuggestion) => void;
};

type ComponentProps = {
  doc: DocumentModel;
  inputProps: Omit<NodeTextFieldProps, "value"> & {
    value?: string;
  };
  suggestionsProps?: ValueSuggestionsProps;
  initialIndex?: number;
  providerSearchOptions?: ProviderSearchOptions;
  onSelect?: (suggestion: SingleSuggestion) => void;
  onSubmit?: (value: string) => void;
};

export const NodeValueSuggestionsFieldComponent = observer(
  function NodeValueSuggestionsFieldComponent({
    doc,
    inputProps: { value, className, ...inputProps },
    suggestionsProps,
    onSubmit = () => {},
  }: ComponentProps) {
    const [opened, setOpened] = useState(false);
    const [inputValue, setInputValue] = useState<string>(value ?? "");
    const { suggestionsAnchorId, accept } = useSuggestionsContext();
    const t = useTranslations("sidebar.activeNode");

    const open = !!(opened && (value || inputValue) && suggestionsProps);
    const { refs, context } = useSuggestionsFloating({
      open,
      options: {
        offsetOptions: {
          mainAxis: 2,
        },
      },
    });

    const { onSelect } = suggestionsProps ?? {};

    useEffect(() => {
      setInputValue(value ?? "");
    }, [value]);

    const reset = () => {
      setInputValue(value ?? "");
      setOpened(false);
    };

    const handleSelect = (suggestion: Suggestion) => {
      if (suggestion.type === "single") {
        onSelect?.(suggestion);
        doc.editor.suggestions.reset();
      }

      reset();
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value.replace(/\s/g, "");

      setInputValue(newValue);

      if (suggestionsProps) {
        setOpened(!!newValue);
      }
    };

    const handleSubmit = () => {
      if (inputValue.length > 0) {
        onSubmit(inputValue);
      }

      reset();
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (keyMatcher("Escape", e)) {
        reset();
        return;
      }

      if (!doc.editor.suggestions.activeSuggestion && keyMatcher("Enter", e)) {
        handleSubmit();
      }
    };

    const handleBlur = () => {
      if (!doc.editor.suggestions.activeSuggestion) {
        handleSubmit();
      }
    };

    return (
      <div>
        <NodeTextField
          value={inputValue}
          name={inputProps.name ?? t("fields.symbol")}
          aria-label={inputProps?.labelProps?.label ? undefined : t("fields.symbol")}
          className="font-mono"
          onChange={handleChange}
          onFocus={() => setOpened(true)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          {...inputProps}
          labelProps={{
            label: t("fields.symbol"),
            className: twMerge("shadow-block dark:shadow-block-inverted", className),
            ...inputProps?.labelProps,
          }}
        />
        <div ref={refs.setReference} />
        <FloatingPortal id={suggestionsAnchorId}>
          {open && (
            <Suggestions
              {...suggestionsProps}
              ref={refs.setFloating}
              value={inputValue}
              doc={doc}
              accept={accept}
              onSelect={handleSelect}
              floatingContext={context}
              className="min-w-[280px] max-w-[280px]"
            />
          )}
        </FloatingPortal>
      </div>
    );
  },
);

type Props = ComponentProps & {
  accept?: SuggestionProviderType[];
};

export const NodeValueSuggestionsField = observer(function NodeValueSuggestionsField(props: Props) {
  const { accept } = props.suggestionsProps ?? {};

  return (
    <SuggestionsProvider accept={accept}>
      <NodeValueSuggestionsFieldComponent {...props} />
    </SuggestionsProvider>
  );
});
